import { landlordApi } from "@/api/LandlordApi";
import { userApi } from "@/api/UserApi";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Button } from "@/components/ui/shadcn/button";
import { UserType } from "@/types/IdentityTypes";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { RoleCard } from "./RoleCard";
import { Check, Building2, Handshake, RefreshCw } from "lucide-react";
import { animDelay } from "@/lib/utils";

// Renter is not an account (2026-09-01): renters live on WhatsApp with Hazel
// and their web moments ride the signing deep link.
type RoleChoice = "Landlord" | "Manager";

const SelectExperiencePage = () => {
  const [role, setRole] = useState<RoleChoice>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { user, setUserType } = useAuth();

  const isVerified = user?.profile?.IsVerified === "True";

  // Redirect if user already has a role
  useEffect(() => {
    if (
      user?.profile?.UserType &&
      user.profile.UserType !== UserType.Unspecified
    ) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!role) {
        setError("Please select an experience to continue");
        return;
      }

      try {
        setLoading(true);
        await userApi.setUserRole(role);
        const nextUserType =
          role === "Manager" ? UserType.Manager : UserType.Landlord;
        await setUserType(nextUserType);

        // Managers ride the landlord sync route until they get their own —
        // it upserts the User row required by the property-creation FK.
        await landlordApi.syncProfileFromIdentity();

        navigate("/");
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [role, navigate, setUserType],
  );

  const setRoleToLandlord = useCallback(() => {
    setRole("Landlord");
    setError(undefined);
  }, []);

  const setRoleToManager = useCallback(() => {
    setRole("Manager");
    setError(undefined);
  }, []);

  // Stepper steps
  const steps = isVerified
    ? [
        { label: "Sign Up", completed: true },
        { label: "Choose Experience", active: true },
      ]
    : [
        { label: "Sign Up", completed: true },
        { label: "Verify Email", completed: true },
        { label: "Choose Experience", active: true },
      ];

  return (
    <div className="auth-shell relative flex h-[100dvh] flex-col overflow-hidden bg-ink">
      {/* Subtle ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-primary/[0.04] blur-3xl" />
        <div className="absolute -bottom-32 right-0 h-[300px] w-[500px] rounded-full bg-primary/[0.03] blur-3xl" />
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-8">
        <div
          className="anim-pop-in relative w-full max-w-[1000px]
            rounded-2xl bg-bg-light p-4 sm:rounded-3xl sm:p-10 md:p-12
            shadow-[var(--shadow-depth-md)]
            ring-1 ring-gray-300/60
            before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px
            before:rounded-t-2xl before:bg-gradient-to-r before:from-transparent before:via-white/50 before:to-transparent
            sm:before:rounded-t-3xl"
        >
          {/* Stepper */}
          <div
            className="anim-slide-down mb-6 flex items-center justify-center gap-2 sm:mb-10"
            style={animDelay(150)}
          >
            {steps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-2">
                {i > 0 && (
                  <div
                    className="anim-scale-x h-0.5 w-8 sm:w-12"
                    style={animDelay(250 + i * 100)}
                  >
                    <div
                      className={`h-full w-full ${step.completed || step.active ? "bg-primary" : "bg-gray-300"}`}
                    />
                  </div>
                )}
                <div
                  className={`anim-scale-in flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold sm:h-8 sm:w-8 ${
                    step.completed
                      ? "bg-primary text-primary-foreground"
                      : step.active
                        ? "bg-primary/10 text-primary ring-2 ring-primary"
                        : "bg-gray-200 text-gray-500"
                  }`}
                  style={animDelay(200 + i * 100)}
                >
                  {step.completed ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  ) : (
                    i + 1
                  )}
                </div>
              </div>
            ))}
          </div>

          <header
            className="anim-slide-up mb-5 flex flex-col gap-2 text-center sm:mb-8 sm:gap-4"
            style={animDelay(200)}
          >
            <h1 className="text-2xl font-medium tracking-tight text-foreground sm:text-4xl">
              How will you use Rentinit?
            </h1>
            <p className="text-sm text-gray-600 sm:text-lg">
              Pick your starting experience.
            </p>
            <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
              <RefreshCw className="h-3.5 w-3.5" />
              <span>Switch anytime from the dashboard</span>
            </div>
          </header>

          <main className="w-full">
            <form
              id="select-role-form"
              className="w-full"
              onSubmit={handleSubmit}
            >
              <div
                role="radiogroup"
                className="grid w-full grid-cols-1 items-stretch gap-3 sm:gap-6 md:grid-cols-2"
                aria-required="true"
              >
                <div className="anim-pop-in flex" style={animDelay(400)}>
                  <RoleCard
                    icon={
                      <Building2 className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                    }
                    title="I own a property"
                    description="List, lease & get paid"
                    features={[
                      "Manage your portfolio",
                      "Screen tenants & offers",
                      "Contracts & payments",
                    ]}
                    isSelected={role === "Landlord"}
                    onClick={setRoleToLandlord}
                  />
                </div>
                <div className="anim-pop-in flex" style={animDelay(500)}>
                  <RoleCard
                    icon={
                      <Handshake className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                    }
                    title="I manage for someone else"
                    description="Help an owner list, lease & coordinate"
                    features={[
                      "Act on the owner's behalf",
                      "Handle leads & viewings",
                      "Owner signs the contracts",
                    ]}
                    isSelected={role === "Manager"}
                    onClick={setRoleToManager}
                  />
                </div>
              </div>
              {error && (
                <p className="anim-slide-down mt-6 text-center text-sm font-medium text-destructive">
                  {error}
                </p>
              )}
            </form>
          </main>
          <Link
            to="/reports"
            className="mt-6 block text-center text-sm text-brand-ink"
          >
            Open Reports
          </Link>
        </div>
      </div>

      <footer
        className="anim-slide-up relative flex items-center border-t border-gray-300/60 bg-bg-light/80 px-6 py-5 shadow-[var(--shadow-depth-sm)] backdrop-blur-sm sm:py-6"
        style={animDelay(400)}
      >
        <Button
          className="mx-auto h-12 w-full max-w-xl text-base font-semibold"
          type="submit"
          form="select-role-form"
          disabled={loading}
        >
          {loading ? <LoadingDots color="current" /> : "Continue"}
        </Button>
      </footer>
    </div>
  );
};

export default SelectExperiencePage;
